/**
 * dialog — the ONE dialog and toast system for the portal.
 *
 * 🔴 WHY THIS EXISTS. The portal had 496 SweetAlert pop-ups across 92 files,
 * 11 files on the in-app ModalKit, and three raw `alert()`s. SweetAlert renders
 * outside the design system — its own font, its own animation, its own buttons
 * — so the moments that matter most (confirm, error, success) were the moments
 * that looked least like Inikru.
 *
 * This module is a DROP-IN for `sweetalert2`'s surface, rendered on ModalKit:
 *
 *   import dialog from '../utils/dialog';
 *   dialog.fire({ title, text, icon, showCancelButton, confirmButtonText })
 *     .then(({ isConfirmed, value }) => …)
 *   dialog.fire('Saved', 'Your changes are live.', 'success')
 *   dialog.mixin({ toast: true, position: 'top-end', timer: 2500 }).fire(…)
 *   dialog.showLoading() / dialog.close() / dialog.showValidationMessage(msg)
 *
 * The call shapes are SweetAlert's on purpose, so retiring it is an import
 * swap, not 496 rewrites. What it deliberately does NOT do:
 *
 *   • `confirmButtonColor` / `customClass` / `width` / `background` — ignored.
 *     There is one look now; per-call restyling is the thing being removed.
 *   • A success with a `timer` (or `toast: true`) becomes a TOAST — bottom
 *     centre, quiet, no button — because a modal that says "Saved!" and makes
 *     you click OK is friction dressed as feedback.
 *   • `html:` strings are rendered after a light sanitise (no <script>, no
 *     on*= handlers, no javascript: URLs). They are app-authored, never user
 *     input; keep it that way.
 *
 * Resolves `{ isConfirmed, isDenied, isDismissed, value, dismiss }` exactly as
 * SweetAlert does, so `.then(r => { if (r.isConfirmed) … })` keeps working.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  CheckCircle2, AlertTriangle, XCircle, Info, HelpCircle, Loader2, X,
} from 'lucide-react';
import { Modal, BtnPrimary, BtnGhost, INPUT, SELECT } from '../components/ModalKit';

// ── the store ───────────────────────────────────────────────────────────────
// Module-level so any code can open a dialog without a provider. One modal at
// a time (a second `fire` while one is open dismisses the first — SweetAlert's
// behaviour), any number of toasts.
const state = { modal: null, toasts: [], listeners: new Set(), seq: 0 };
const emit = () => state.listeners.forEach((fn) => fn());

const ICONS = {
  success: { Icon: CheckCircle2, color: '#3D6B4E', bg: '#E9F3ED' },
  error: { Icon: XCircle, color: '#DC2626', bg: '#FDECEC' },
  warning: { Icon: AlertTriangle, color: '#A8690C', bg: '#FBF1E1' },
  info: { Icon: Info, color: '#1B1B1B', bg: '#F0F0EE' },
  question: { Icon: HelpCircle, color: '#B40A0A', bg: '#FDF3F3' },
};

export const DismissReason = Object.freeze({
  cancel: 'cancel', backdrop: 'backdrop', close: 'close', esc: 'esc', timer: 'timer',
});

// App-authored HTML only. This strips the three things that would turn a
// string into a script, and nothing else — it is not a user-content sanitiser.
const sanitize = (html) => String(html)
  .replace(/<script[\s\S]*?<\/script>/gi, '')
  .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
  .replace(/javascript:/gi, '');

// SweetAlert accepts fire(title, text, icon) as well as fire({...}).
const normalise = (a, b, c) => {
  if (typeof a === 'string') return { title: a, text: b, icon: c };
  return { ...(a || {}) };
};

// A success that closes itself is feedback, not a question — it is a toast.
const isToastLike = (o) => Boolean(
  o.toast || (o.timer && !o.showCancelButton && !o.showDenyButton && !o.input && !o.preConfirm),
);

// ── public API ──────────────────────────────────────────────────────────────
function fire(a, b, c) {
  const opts = normalise(a, b, c);
  ensureHost();
  if (isToastLike(opts)) return pushToast(opts);
  return new Promise((resolve) => {
    if (state.modal) {
      // SweetAlert replaces the open popup; the old promise resolves dismissed.
      state.modal.resolve({ isConfirmed: false, isDenied: false, isDismissed: true, dismiss: DismissReason.close });
    }
    state.modal = { id: ++state.seq, opts, resolve, busy: Boolean(opts.loadingOnOpen), validation: '' };
    emit();
  });
}

function pushToast(opts) {
  const id = ++state.seq;
  const timer = Number(opts.timer) > 0 ? Number(opts.timer) : 2800;
  state.toasts = [...state.toasts, { id, opts, timer }];
  emit();
  return new Promise((resolve) => {
    setTimeout(() => {
      state.toasts = state.toasts.filter((t) => t.id !== id);
      emit();
      resolve({ isConfirmed: false, isDenied: false, isDismissed: true, dismiss: DismissReason.timer });
    }, timer);
  });
}

function settle(result) {
  const m = state.modal;
  if (!m) return;
  state.modal = null;
  emit();
  try { m.opts.willClose?.(); } catch (e) { /* app callback */ }
  m.resolve(result);
  try { m.opts.didClose?.(); } catch (e) { /* app callback */ }
}

function close() { settle({ isConfirmed: false, isDenied: false, isDismissed: true, dismiss: DismissReason.close }); }

// `Swal.showLoading()` with no dialog open shows a blocking "please wait";
// with one open it just marks it busy (SweetAlert disables the buttons).
function showLoading() {
  ensureHost();
  if (!state.modal) {
    state.modal = {
      id: ++state.seq, resolve: () => {}, busy: true, validation: '',
      opts: { title: 'Please wait…', loadingOnly: true, allowOutsideClick: false },
    };
  } else {
    state.modal.busy = true;
  }
  emit();
}
function hideLoading() { if (state.modal) { state.modal.busy = false; emit(); } }
function isLoading() { return Boolean(state.modal?.busy); }
function isVisible() { return Boolean(state.modal); }
function showValidationMessage(msg) { if (state.modal) { state.modal.validation = String(msg || ''); state.modal.busy = false; emit(); } }
function resetValidationMessage() { showValidationMessage(''); }
function getInput() { return document.querySelector('[data-dialog-input]'); }
function getPopup() { return document.querySelector('[data-dialog-popup]'); }
// `Swal.update({ title, text })` — change the open dialog in place.
function update(patch = {}) {
  if (!state.modal) return;
  state.modal.opts = { ...state.modal.opts, ...patch };
  emit();
}
function mixin(defaults = {}) {
  return {
    fire: (a, b, c) => fire({ ...defaults, ...normalise(a, b, c) }),
    mixin: (more) => mixin({ ...defaults, ...more }),
  };
}

const dialog = {
  fire, close, showLoading, hideLoading, isLoading, isVisible,
  showValidationMessage, resetValidationMessage, getInput, getPopup, mixin,
  DismissReason,
  // Sugar for new code — clearer than the SweetAlert shapes.
  confirm: (title, text, extra = {}) => fire({
    title, text, icon: 'question', showCancelButton: true, confirmButtonText: 'Yes', ...extra,
  }).then((r) => r.isConfirmed),
  toast: (title, icon = 'success', extra = {}) => fire({ title, icon, timer: 2500, ...extra }),
  alert: (title, text, icon = 'info') => fire({ title, text, icon }),
};
export default dialog;

// ── host ────────────────────────────────────────────────────────────────────
let hostMounted = false;
function ensureHost() {
  if (hostMounted || typeof document === 'undefined') return;
  hostMounted = true;
  const el = document.createElement('div');
  el.id = 'inikru-dialog-root';
  document.body.appendChild(el);
  createRoot(el).render(<Host />);
}

function useStore() {
  const [, tick] = useState(0);
  useEffect(() => {
    const fn = () => tick((n) => n + 1);
    state.listeners.add(fn);
    return () => state.listeners.delete(fn);
  }, []);
  return state;
}

function Host() {
  const s = useStore();
  return (
    <>
      {s.modal && <DialogModal key={s.modal.id} m={s.modal} />}
      <ToastStack toasts={s.toasts} />
    </>
  );
}

// ── the modal ───────────────────────────────────────────────────────────────
function DialogModal({ m }) {
  const { opts } = m;
  const tone = ICONS[opts.icon] || null;
  const [value, setValue] = useState(() => {
    if (opts.input === 'select') {
      const first = optionsOf(opts.inputOptions)[0];
      return opts.inputValue ?? (first ? first[0] : '');
    }
    if (opts.input === 'checkbox') return Boolean(opts.inputValue);
    return opts.inputValue ?? '';
  });
  const inputRef = useRef(null);
  const confirmRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    (opts.input ? inputRef.current : confirmRef.current)?.focus?.();
    try { opts.didOpen?.(popupRef.current); } catch (e) { /* app callback */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = (reason) => {
    if (m.busy && opts.loadingOnly) return;
    settle({ isConfirmed: false, isDenied: false, isDismissed: true, dismiss: reason });
  };

  const confirm = async () => {
    if (m.busy) return;
    let v = opts.input ? value : true;
    if (opts.input && opts.inputValidator) {
      const msg = await opts.inputValidator(v);
      if (msg) { showValidationMessage(msg); return; }
    }
    if (opts.preConfirm) {
      m.busy = true; m.validation = ''; emit();
      let out;
      try { out = await opts.preConfirm(v); } catch (err) {
        showValidationMessage(err?.message || 'Something went wrong.');
        return;
      }
      if (!state.modal || state.modal.id !== m.id) return;   // preConfirm closed it
      if (out === false) { m.busy = false; emit(); return; }
      if (out !== undefined) v = out;
    }
    settle({ isConfirmed: true, isDenied: false, isDismissed: false, value: v });
  };

  const deny = () => settle({ isConfirmed: false, isDenied: true, isDismissed: false });

  const allowOutside = opts.allowOutsideClick !== false && !opts.loadingOnly;
  const cancelText = opts.cancelButtonText || 'Cancel';
  const confirmText = opts.confirmButtonText || 'OK';
  const showConfirm = opts.showConfirmButton !== false && !opts.loadingOnly;

  const body = (
    <div data-dialog-popup ref={popupRef} className="space-y-4">
      {opts.text && <p className="text-[15px] text-ink-soft leading-relaxed text-gray-700">{opts.text}</p>}
      {opts.html && (React.isValidElement(opts.html)
        ? opts.html
        : <div className="text-[15px] text-gray-700 leading-relaxed [&_b]:font-semibold [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_a]:underline"
            dangerouslySetInnerHTML={{ __html: sanitize(opts.html) }} />)}
      {opts.loadingOnly && (
        <div className="flex items-center gap-3 text-gray-600 text-sm py-2">
          <Loader2 size={18} className="animate-spin" /> {opts.text || 'This will only take a moment.'}
        </div>
      )}
      {opts.input && (
        <div>
          {opts.inputLabel && <label className="block text-xs font-bold uppercase tracking-[0.12em] text-muted mb-1.5">{opts.inputLabel}</label>}
          {opts.input === 'textarea' ? (
            <textarea ref={inputRef} data-dialog-input rows={4} className={INPUT} value={value}
              placeholder={opts.inputPlaceholder || ''} onChange={(e) => setValue(e.target.value)} />
          ) : opts.input === 'select' ? (
            <select ref={inputRef} data-dialog-input className={SELECT} value={value} onChange={(e) => setValue(e.target.value)}>
              {opts.inputPlaceholder && <option value="" disabled>{opts.inputPlaceholder}</option>}
              {optionsOf(opts.inputOptions).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          ) : opts.input === 'checkbox' ? (
            <label className="inline-flex items-center gap-2 text-sm text-ink">
              <input ref={inputRef} data-dialog-input type="checkbox" className="accent-ink" checked={Boolean(value)}
                onChange={(e) => setValue(e.target.checked)} />
              {opts.inputPlaceholder}
            </label>
          ) : (
            <input ref={inputRef} data-dialog-input type={opts.input === 'number' ? 'number' : opts.input} className={INPUT}
              value={value} placeholder={opts.inputPlaceholder || ''}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirm(); } }}
              {...(opts.inputAttributes || {})} />
          )}
        </div>
      )}
      {m.validation && (
        <p role="alert" className="text-sm font-medium text-[#DC2626] bg-[#FDECEC] rounded-[10px] px-3 py-2">{m.validation}</p>
      )}
      {opts.footer && (React.isValidElement(opts.footer)
        ? <div className="text-xs text-muted">{opts.footer}</div>
        : <div className="text-xs text-muted" dangerouslySetInnerHTML={{ __html: sanitize(opts.footer) }} />)}
    </div>
  );

  const footer = opts.loadingOnly ? null : (
    <>
      {opts.showCancelButton && <BtnGhost onClick={() => dismiss(DismissReason.cancel)}>{cancelText}</BtnGhost>}
      {opts.showDenyButton && (
        <button type="button" onClick={deny}
          className="h-11 px-5 rounded-full border border-[#DC2626] text-sm font-semibold text-[#DC2626] hover:bg-[#FDECEC] transition-colors">
          {opts.denyButtonText || 'No'}
        </button>
      )}
      {showConfirm && (
        <span ref={confirmRef} tabIndex={-1} className="inline-flex">
          <BtnPrimary onClick={confirm} busy={m.busy}>{confirmText}</BtnPrimary>
        </span>
      )}
    </>
  );

  return (
    <Modal
      icon={tone ? tone.Icon : undefined}
      title={opts.title || (opts.icon === 'error' ? 'Something went wrong' : '')}
      subtitle={opts.titleText || undefined}
      onClose={() => (allowOutside ? dismiss(DismissReason.close) : undefined)}
      footer={footer}
      maxW="sm:max-w-md"
    >
      {body}
    </Modal>
  );
}

function optionsOf(inputOptions) {
  if (!inputOptions) return [];
  if (inputOptions instanceof Map) return [...inputOptions.entries()];
  return Object.entries(inputOptions);
}

// ── toasts ──────────────────────────────────────────────────────────────────
function ToastStack({ toasts }) {
  if (!toasts.length) return null;
  return (
    <div aria-live="polite" role="status"
      className="fixed inset-x-0 bottom-24 lg:bottom-6 z-[10001] flex flex-col items-center gap-2 px-4 pointer-events-none">
      {toasts.map((t) => <Toast key={t.id} t={t} />)}
    </div>
  );
}

function Toast({ t }) {
  const { opts } = t;
  const tone = ICONS[opts.icon] || ICONS.info;
  const Icon = tone.Icon;
  const remove = () => { state.toasts = state.toasts.filter((x) => x.id !== t.id); emit(); };
  return (
    <div className="pointer-events-auto flex items-start gap-3 max-w-[92vw] sm:max-w-md w-auto bg-white border-[1.5px] border-ink rounded-[12px] px-4 py-3 shadow-[4px_4px_0_#1B1B1B] motion-safe:animate-fadeIn">
      <span className="w-8 h-8 rounded-lg grid place-items-center flex-shrink-0" style={{ background: tone.bg, color: tone.color }}>
        <Icon size={17} />
      </span>
      <div className="min-w-0 flex-1">
        {opts.title && <p className="text-sm font-bold text-ink leading-snug">{opts.title}</p>}
        {(opts.text || opts.html) && (
          opts.text
            ? <p className="text-[13px] text-gray-600 leading-snug mt-0.5">{opts.text}</p>
            : <div className="text-[13px] text-gray-600 leading-snug mt-0.5" dangerouslySetInnerHTML={{ __html: sanitize(opts.html) }} />
        )}
      </div>
      <button type="button" onClick={remove} aria-label="Dismiss"
        className="w-7 h-7 -mr-1 rounded-full grid place-items-center text-gray-400 hover:text-ink hover:bg-gray-100 flex-shrink-0">
        <X size={14} />
      </button>
    </div>
  );
}
